import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Edit, Trash, ArrowLeft, AlertCircle } from 'lucide-react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import ChildProfileCard from '@/components/child/ChildProfileCard';
import QuotesList from '@/components/quotes/QuotesList';
import EditProfileDialog from '@/components/child/EditProfileDialog';
import DeleteConfirmDialog from '@/components/child/DeleteConfirmDialog';
import { useChildData } from '@/hooks/useChildData';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

const ChildDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [tempPhotoFile, setTempPhotoFile] = useState<File | null>(null);
  
  const {
    child,
    editedChild,
    setEditedChild,
    quotes,
    isLoading,
    isUploading,
    updateChild,
    updatePhoto,
    deleteChild,
    addQuote
  } = useChildData(id, user?.id);

  useEffect(() => {
    logger.log("ChildDetails rendering:", { child, isLoading, quotes });
    
    // If loading takes too long, trigger a retry
    if (isLoading) {
      const timer = setTimeout(() => {
        if (loadAttempts < 3) {
          logger.log(`Still loading after attempt ${loadAttempts + 1}, retrying...`);
          setLoadAttempts(prev => prev + 1);
        } else if (loadAttempts === 3) {
          toast({
            title: "Loading issue",
            description: "Having trouble loading the profile. Please try refreshing the page.",
            variant: "destructive"
          });
        }
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [child, isLoading, quotes, loadAttempts, toast]);

  const handleSaveChanges = async () => {
    let success = await updateChild();
    
    // If we have a temporary photo file, upload it after updating the child info
    if (success && tempPhotoFile) {
      success = await updatePhoto(tempPhotoFile);
      setTempPhotoFile(null);
    }
    
    if (success) {
      setIsEditing(false);
    } else {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteChild = async () => {
    const success = await deleteChild();
    if (success) {
      toast({
        title: "Success",
        description: "Profile deleted successfully!"
      });
      navigate('/children');
    } else {
      toast({
        title: "Error",
        description: "Failed to delete profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleUpdatePhoto = async (file: File) => {
    await updatePhoto(file);
  };

  const handleEditPhotoChange = (file: File) => {
    setTempPhotoFile(file);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p>Loading child profile...</p>
          {loadAttempts > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Attempt {loadAttempts + 1}... 
            </p>
          )}
          {loadAttempts >= 2 && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4" 
              onClick={() => navigate('/children')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Children
            </Button>
          )}
        </div>
      </Layout>
    );
  }

  if (!child) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <p className="text-lg">Child profile not found</p>
          <p className="text-sm text-muted-foreground">
            The profile you're looking for couldn't be loaded or doesn't exist.
          </p>
          <Button variant="outline" onClick={() => navigate('/children')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Children
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-16">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigate('/children')}
              className="mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">{child.name}'s Profile</h1>
          </div>
          <div className="space-x-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-500 hover:bg-red-50"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ChildProfileCard 
            child={child} 
            onUpdatePhoto={handleUpdatePhoto} 
          />
          <QuotesList 
            quotes={quotes} 
            childId={child.id} 
            onQuoteAdded={addQuote} 
          />
        </div>
      </div>
      
      <EditProfileDialog 
        isOpen={isEditing}
        onOpenChange={setIsEditing}
        child={editedChild}
        onChildChange={setEditedChild}
        onSave={handleSaveChanges}
        onPhotoChange={handleEditPhotoChange}
      />
      
      <DeleteConfirmDialog 
        isOpen={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        childName={child.name}
        onDelete={handleDeleteChild}
      />
    </Layout>
  );
};

export default ChildDetails;
